import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import TopBar from '../components/layout/TopBar';
import api from '../utils/api';
import toast from 'react-hot-toast';

const daysUntil = (d) => d ? Math.ceil((new Date(d) - new Date()) / 86400000) : null;
const fmt = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const checkTime = (d) => d
  ? new Date(d).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  : '';

const checkDateTime = (check) => {
  const stamp = check.updated_at || check.created_at;
  return `${fmt(check.check_date)}${stamp ? ` · ${checkTime(stamp)}` : ''}`;
};

const checkStatusLabel = status => ({
  open: 'Concern',
  improving: 'Improving',
  no_change: 'No change',
  worse: 'Worse',
  resolved: 'Resolved',
  normal: 'Normal'
}[status] || 'Concern');

const checkConcernSummary = (check) => {
  const fields = [
    ['general_condition', {
      quiet: 'Quiet / subdued',
      agitated: 'Agitated',
      unwell: 'Unwell'
    }],
    ['appetite', {
      reduced: 'Reduced appetite',
      not_eating: 'Not eating'
    }],
    ['water', {
      reduced: 'Reduced water',
      excessive: 'Excessive drinking'
    }],
    ['manure_urination', {
      loose: 'Loose manure',
      dry: 'Dry manure',
      none_observed: 'No manure observed',
      urination_concern: 'Urination concern'
    }],
    ['movement', {
      stiff: 'Stiff',
      lame: 'Lame',
      reluctant: 'Reluctant to move'
    }],
    ['legs_feet', {
      heat: 'Heat',
      swelling: 'Swelling',
      wound: 'Wound',
      hoof_concern: 'Hoof concern'
    }],
  ];

  const parts = [];

  fields.forEach(([key, labels]) => {
    const value = check?.[key];

    if (value && value !== 'normal' && labels[value]) {
      parts.push(labels[value]);
    }
  });

  return parts.join(' · ')
    || (check?.notes ? 'Concern noted' : 'Concern recorded');
};

const detailLabel = (key, value) => {
  const labels = {
    general_condition: {
      normal: 'Normal',
      quiet: 'Quiet / subdued',
      agitated: 'Agitated',
      unwell: 'Unwell'
    },
    appetite: {
      normal: 'Normal',
      reduced: 'Reduced',
      not_eating: 'Not eating'
    },
    water: {
      normal: 'Normal',
      reduced: 'Reduced',
      excessive: 'Excessive'
    },
    manure_urination: {
      normal: 'Normal',
      loose: 'Loose manure',
      dry: 'Dry manure',
      none_observed: 'No manure observed',
      urination_concern: 'Urination concern'
    },
    movement: {
      normal: 'Normal',
      stiff: 'Stiff',
      lame: 'Lame',
      reluctant: 'Reluctant to move'
    },
    legs_feet: {
      normal: 'Normal',
      heat: 'Heat',
      swelling: 'Swelling',
      wound: 'Wound',
      hoof_concern: 'Hoof concern'
    },
  };

  return labels[key]?.[value] || value || '—';
};

const DueBadge = ({ days }) => {
  if (days === null) return null;
  if (days < 0) return <span className="badge badge-red">Overdue</span>;
  if (days <= 7) return <span className="badge badge-red">{days}d</span>;
  if (days <= 30) return <span className="badge badge-amber">{days}d</span>;
  return <span className="badge badge-green">{days}d</span>;
};

const DueBar = ({ days, total }) => {
  const pct = Math.min(100, Math.max(0, 100 - ((Math.max(0, days) / total) * 100)));
  const color = days < 0 ? 'var(--cr-red)' : days <= 7 ? 'var(--cr-red)' : days <= 30 ? 'var(--cr-amber)' : 'var(--cr-green)';
  return <div className="progress-bar"><div className="progress-fill" style={{ width: `${pct}%`, background: color }} /></div>;
};

// ── ADD VACCINE MODAL ──
const AddVaccineModal = ({ horseId, onClose, onSave }) => {
  const [form, setForm] = useState({ name: '', date_given: new Date().toISOString().split('T')[0], given_by: '', lot_number: '', notes: '', frequency_months: '12', reminder_days_before: '30' });
  const [saving, setSaving] = useState(false);
  const set = f => e => setForm(p => ({ ...p, [f]: e.target.value }));
  const COMMON = ['West Nile','EEE / WEE','Rabies','Influenza','Rhinopneumonitis','Tetanus','Strangles','Potomac Horse Fever'];
  const handleSave = async () => {
    if (!form.name || !form.date_given) return toast.error('Name and date required');
    setSaving(true);
    try {
      const { data } = await api.post(`/health/${horseId}/vaccines`, form);
      toast.success('Vaccine logged!');
      onSave(data);
    } catch { toast.error('Failed to save'); } finally { setSaving(false); }
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 999, display: 'flex', alignItems: 'flex-end' }}>
      <div style={{ background: 'white', borderRadius: '20px 20px 0 0', padding: 20, width: '100%', maxWidth: 480, margin: '0 auto', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <h2>Log vaccine</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--text-secondary)' }}>×</button>
        </div>
        <div className="form-group">
          <label className="form-label">Vaccine name *</label>
          <div className="chip-group" style={{ marginBottom: 8 }}>
            {COMMON.map(v => <div key={v} className={`chip ${form.name === v ? 'selected' : ''}`} onClick={() => setForm(p => ({ ...p, name: v }))} style={{ fontSize: 11 }}>{v}</div>)}
          </div>
          <input className="form-input" placeholder="Or type custom..." value={form.name} onChange={set('name')} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div className="form-group">
            <label className="form-label">Date given *</label>
            <input className="form-input" type="date" value={form.date_given} onChange={set('date_given')} />
          </div>
          <div className="form-group">
            <label className="form-label">Given by</label>
            <input className="form-input" placeholder="Vet name" value={form.given_by} onChange={set('given_by')} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Lot number (optional)</label>
          <input className="form-input" placeholder="For your records" value={form.lot_number} onChange={set('lot_number')} />
        </div>
        <div className="form-group">
          <label className="form-label">Repeat reminder</label>
          <select className="form-input" value={form.frequency_months} onChange={set('frequency_months')}>
            <option value="">No reminder</option>
            <option value="6">Every 6 months</option>
            <option value="12">Annual (12 months)</option>
            <option value="18">Every 18 months</option>
          </select>
        </div>
        {form.frequency_months && (
          <div className="form-group">
            <label className="form-label">Remind me</label>
            <select className="form-input" value={form.reminder_days_before} onChange={set('reminder_days_before')}>
              <option value="7">1 week before</option>
              <option value="14">2 weeks before</option>
              <option value="30">30 days before</option>
              <option value="60">60 days before</option>
            </select>
          </div>
        )}
        <div className="form-group">
          <label className="form-label">Notes</label>
          <input className="form-input" placeholder="e.g. No reaction, given in left neck" value={form.notes} onChange={set('notes')} />
        </div>
        <button className="btn btn-primary btn-full" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save vaccine & set reminder'}</button>
      </div>
    </div>
  );
};

// ── ADD WORMER MODAL ──
const AddWormerModal = ({ horseId, onClose, onSave }) => {
  const [form, setForm] = useState({ product_name: '', active_ingredient: '', date_given: new Date().toISOString().split('T')[0], horse_weight_lbs: '', notes: '', interval_weeks: '13', reminder_days_before: '7' });
  const [saving, setSaving] = useState(false);
  const set = f => e => setForm(p => ({ ...p, [f]: e.target.value }));
  const COMMON = ['Quest Plus (moxidectin)','Zimecterin Gold','Panacur (fenbendazole)','Ivermectin','Equimax'];
  const handleSave = async () => {
    if (!form.product_name || !form.date_given) return toast.error('Product and date required');
    setSaving(true);
    try {
      const payload = { ...form, product_name: form.product_name.split('(')[0].trim() };
      const { data } = await api.post(`/health/${horseId}/wormers`, payload);
      toast.success('Worming logged!');
      onSave(data);
    } catch { toast.error('Failed to save'); } finally { setSaving(false); }
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 999, display: 'flex', alignItems: 'flex-end' }}>
      <div style={{ background: 'white', borderRadius: '20px 20px 0 0', padding: 20, width: '100%', maxWidth: 480, margin: '0 auto', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <h2>Log worming</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer' }}>×</button>
        </div>
        <div className="form-group">
          <label className="form-label">Product *</label>
          <div className="chip-group" style={{ marginBottom: 8 }}>
            {COMMON.map(v => <div key={v} className={`chip ${form.product_name === v ? 'selected' : ''}`} onClick={() => setForm(p => ({ ...p, product_name: v }))} style={{ fontSize: 11 }}>{v}</div>)}
          </div>
          <input className="form-input" placeholder="Or type product name..." value={form.product_name} onChange={set('product_name')} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div className="form-group">
            <label className="form-label">Date given *</label>
            <input className="form-input" type="date" value={form.date_given} onChange={set('date_given')} />
          </div>
          <div className="form-group">
            <label className="form-label">Horse weight (lbs)</label>
            <input className="form-input" type="number" placeholder="1182" value={form.horse_weight_lbs} onChange={set('horse_weight_lbs')} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Repeat every</label>
          <select className="form-input" value={form.interval_weeks} onChange={set('interval_weeks')}>
            <option value="">No repeat</option>
            <option value="8">Every 8 weeks</option>
            <option value="13">Every 3 months</option>
            <option value="26">Every 6 months</option>
            <option value="52">Annual</option>
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Notes</label>
          <input className="form-input" placeholder="e.g. Full dose, no issues" value={form.notes} onChange={set('notes')} />
        </div>
        <button className="btn btn-primary btn-full" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save & schedule reminder'}</button>
      </div>
    </div>
  );
};

// ── ADD WEIGHT MODAL ──
const AddMedicationModal = ({ horseId, onClose, onSave }) => {
  const [form, setForm] = useState({ name: '', dose_frequency: '', prescribed_by: '', start_date: new Date().toISOString().split('T')[0], end_date: '', is_ongoing: false, reason: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const set = f => e => setForm(p => ({ ...p, [f]: e.target.value }));
  const handleSave = async () => {
    if (!form.name || !form.start_date) return toast.error('Name and start date required');
    setSaving(true);
    try {
      const payload = { ...form, is_active: true, end_date: form.is_ongoing ? null : (form.end_date || null) };
      const { data } = await api.post(`/health/${horseId}/medications`, payload);
      toast.success('Medication added!');
      onSave(data);
    } catch { toast.error('Failed to save'); } finally { setSaving(false); }
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 999, display: 'flex', alignItems: 'flex-end' }}>
      <div style={{ background: 'white', borderRadius: '20px 20px 0 0', padding: 20, width: '100%', maxWidth: 480, margin: '0 auto', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <h2>Add medication</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer' }}>×</button>
        </div>
        <div className="form-group">
          <label className="form-label">Medication name *</label>
          <input className="form-input" placeholder="e.g. Bute, Previcox, SMZ" value={form.name} onChange={set('name')} autoComplete="off" />
        </div>
        <div className="form-group">
          <label className="form-label">Dose & frequency</label>
          <input className="form-input" placeholder="e.g. 1 gram twice daily" value={form.dose_frequency} onChange={set('dose_frequency')} autoComplete="off" />
        </div>
        <div className="form-group">
          <label className="form-label">Prescribed by</label>
          <input className="form-input" placeholder="Vet name" value={form.prescribed_by} onChange={set('prescribed_by')} autoComplete="off" />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div className="form-group">
            <label className="form-label">Start date *</label>
            <input className="form-input" type="date" value={form.start_date} onChange={set('start_date')} />
          </div>
          {!form.is_ongoing && (
            <div className="form-group">
              <label className="form-label">End date</label>
              <input className="form-input" type="date" value={form.end_date} onChange={set('end_date')} />
            </div>
          )}
        </div>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer', fontSize: 13, marginBottom: 14 }}>
          <input type="checkbox" checked={form.is_ongoing} onChange={e => setForm(p => ({ ...p, is_ongoing: e.target.checked }))} style={{ accentColor: 'var(--cr-primary)' }} />
          Ongoing medication (no end date)
        </label>
        <div className="form-group">
          <label className="form-label">Reason</label>
          <input className="form-input" placeholder="e.g. Joint support, ulcer treatment" value={form.reason} onChange={set('reason')} autoComplete="off" />
        </div>
        <div className="form-group">
          <label className="form-label">Notes</label>
          <input className="form-input" placeholder="Any additional notes" value={form.notes} onChange={set('notes')} autoComplete="off" />
        </div>
        <button className="btn btn-primary btn-full" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save medication'}</button>
      </div>
    </div>
  );
};

const AddWeightModal = ({ horseId, onClose, onSave }) => {
  const [form, setForm] = useState({ date: new Date().toISOString().split('T')[0], weight_lbs: '', body_condition_score: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const BCS_COLORS = ['','#E24B4A','#E24B4A','#E24B4A','#BA7517','#1D9E75','#1D9E75','#BA7517','#E24B4A','#E24B4A'];
  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/health/${horseId}/weight`, form);
      toast.success('Weight logged!');
      onSave(data);
    } catch { toast.error('Failed to save'); } finally { setSaving(false); }
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 999, display: 'flex', alignItems: 'flex-end' }}>
      <div style={{ background: 'white', borderRadius: '20px 20px 0 0', padding: 20, width: '100%', maxWidth: 480, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <h2>Log weight</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer' }}>×</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div className="form-group">
            <label className="form-label">Date</label>
            <input className="form-input" type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Weight (lbs)</label>
            <input className="form-input" type="number" placeholder="1182" value={form.weight_lbs} onChange={e => setForm(p => ({ ...p, weight_lbs: e.target.value }))} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Body condition score (Henneke 1–9)</label>
          <p style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>4.5–6 is ideal</p>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {[1,2,3,4,5,6,7,8,9].map(n => (
              <button key={n} onClick={() => setForm(p => ({ ...p, body_condition_score: n }))}
                style={{ width: 36, height: 36, borderRadius: 8, border: `2px solid ${form.body_condition_score === n ? BCS_COLORS[n] : 'var(--border-color)'}`, background: form.body_condition_score === n ? BCS_COLORS[n] : 'white', color: form.body_condition_score === n ? 'white' : 'var(--text-primary)', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
                {n}
              </button>
            ))}
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Notes</label>
          <input className="form-input" placeholder="e.g. Coming out of winter, ribby along topline" value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} />
        </div>
        <button className="btn btn-primary btn-full" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save log'}</button>
      </div>
    </div>
  );
};

// ── ADD DENTAL MODAL ──
const AddDentalModal = ({ horseId, onClose, onSave }) => {
  const today = new Date().toISOString().split('T')[0];
  const addMonths = (dateStr, months) => {
    const d = new Date(`${dateStr}T12:00:00`);
    d.setMonth(d.getMonth() + parseInt(months || 12, 10));
    return d.toISOString().split('T')[0];
  };
  const [form, setForm] = useState({
    date: today,
    dental_type: 'exam_float',
    performed_by: '',
    findings: '',
    notes: '',
    due_months: '12',
    next_due_date: addMonths(today, 12),
    reminder_days_before: '30'
  });
  const [saving, setSaving] = useState(false);
  const set = f => e => setForm(p => ({ ...p, [f]: e.target.value }));
  const updateDate = (value) => setForm(p => ({ ...p, date: value, next_due_date: p.due_months ? addMonths(value, p.due_months) : p.next_due_date }));
  const updateDueMonths = (value) => setForm(p => ({ ...p, due_months: value, next_due_date: value ? addMonths(p.date, value) : p.next_due_date }));
  const handleSave = async () => {
    if (!form.date) return toast.error('Date required');
    setSaving(true);
    try {
      const { data } = await api.post(`/health/${horseId}/dental`, form);
      toast.success('Dental record logged!');
      onSave(data);
    } catch { toast.error('Failed to save'); } finally { setSaving(false); }
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 999, display: 'flex', alignItems: 'flex-end' }}>
      <div style={{ background: 'white', borderRadius: '20px 20px 0 0', padding: 20, width: '100%', maxWidth: 480, margin: '0 auto', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <h2>Log dental</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer' }}>×</button>
        </div>
        <div className="form-group">
          <label className="form-label">Type</label>
          <select className="form-input" value={form.dental_type} onChange={set('dental_type')}>
            <option value="exam">Dental exam</option>
            <option value="float">Floating</option>
            <option value="exam_float">Dental exam + floating</option>
            <option value="extraction">Tooth extraction</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div className="form-group">
            <label className="form-label">Date performed *</label>
            <input className="form-input" type="date" value={form.date} onChange={e => updateDate(e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Performed by</label>
            <input className="form-input" placeholder="Vet or equine dentist" value={form.performed_by} onChange={set('performed_by')} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Findings</label>
          <input className="form-input" placeholder="e.g. Hooks, sharp points, wave mouth" value={form.findings} onChange={set('findings')} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div className="form-group">
            <label className="form-label">Next due</label>
            <select className="form-input" value={form.due_months} onChange={e => updateDueMonths(e.target.value)}>
              <option value="6">6 months</option>
              <option value="12">12 months</option>
              <option value="18">18 months</option>
              <option value="">Custom date</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Due date</label>
            <input className="form-input" type="date" value={form.next_due_date} onChange={set('next_due_date')} />
          </div>
        </div>
        {form.next_due_date && (
          <div className="form-group">
            <label className="form-label">Remind me</label>
            <select className="form-input" value={form.reminder_days_before} onChange={set('reminder_days_before')}>
              <option value="7">7 days before</option>
              <option value="14">14 days before</option>
              <option value="30">30 days before</option>
              <option value="60">60 days before</option>
            </select>
          </div>
        )}
        <div className="form-group">
          <label className="form-label">Notes</label>
          <input className="form-input" placeholder="Sedation, follow-up, owner notes" value={form.notes} onChange={set('notes')} />
        </div>
        <button className="btn btn-primary btn-full" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save dental record'}</button>
      </div>
    </div>
  );
};

// ── MAIN HEALTH PAGE ──
const HealthPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [horse, setHorse] = useState(null);
  const [vaccines, setVaccines] = useState([]);
  const [wormers, setWormers] = useState([]);
  const [medications, setMedications] = useState([]);
  const [weightLogs, setWeightLogs] = useState([]);
  const [dentalLogs, setDentalLogs] = useState([]);
  const [checkHistory, setCheckHistory] = useState([]);
  const [expandedCheck, setExpandedCheck] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [modal, setModal] = useState(null);

  const load = async () => {
    try {
      const [hRes, vRes, wRes, mRes, wlRes, dRes, cRes] = await Promise.all([
        api.get(`/horses/${id}`),
        api.get(`/health/${id}/vaccines`),
        api.get(`/health/${id}/wormers`),
        api.get(`/health/${id}/medications`),
        api.get(`/health/${id}/weight`),
        api.get(`/health/${id}/dental`),
        api.get(`/horse-checks/history/${id}`),
      ]);
      setHorse(hRes.data);
      setVaccines(vRes.data);
      setWormers(wRes.data);
      setMedications(mRes.data);
      setWeightLogs(wlRes.data);
      setDentalLogs(dRes.data);
      setCheckHistory(cRes.data);
    } catch { toast.error('Failed to load health data'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]);

  const latestWeight = weightLogs[0];
  const dueSoon = [
    ...vaccines.filter(v => v.next_due_date && daysUntil(v.next_due_date) <= 30).map(v => ({ ...v, kind: 'vaccine', label: v.name, days: daysUntil(v.next_due_date) })),
    ...wormers.filter(w => w.next_due_date && daysUntil(w.next_due_date) <= 30).map(w => ({ ...w, kind: 'wormer', label: w.product_name, days: daysUntil(w.next_due_date) })),
    ...dentalLogs.filter(d => d.next_due_date && daysUntil(d.next_due_date) <= 30).map(d => ({ ...d, kind: 'dental', label: 'Dental exam/floating', days: daysUntil(d.next_due_date) })),
  ].sort((a, b) => a.days - b.days);

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div>
      <TopBar title={`${horse?.name} — Health`} showBack backTo={`/horses/${id}`}
        rightAction={<button className="btn btn-sm" style={{ background: 'rgba(255,255,255,0.2)', color: 'white', border: 'none' }} onClick={() => setModal('vaccine')}>+ Log</button>} />

      <div className="tab-bar" style={{ overflowX: 'auto', justifyContent: 'flex-start' }}>
        {[['overview','Overview'],['checks','Checks'],['vaccines','Vaccines'],['wormers','Wormers'],['medications','Meds'],['weight','Weight'],['dental','Dental']].map(([val, label]) => (
          <div
            key={val}
            className={`tab-item ${tab === val ? 'active' : ''}`}
            onClick={() => setTab(val)}
            style={{ fontSize: 11, flex: '0 0 auto', minWidth: 64 }}
          >
            {label}
          </div>
        ))}
      </div>

      <div className="page-content">

        {/* OVERVIEW */}
        {tab === 'overview' && (
          <>
            {latestWeight && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
                <div className="card" style={{ textAlign: 'center', padding: '12px 8px' }}>
                  <div style={{ fontSize: 22, fontWeight: 700 }}>{latestWeight.weight_lbs?.toLocaleString() || '—'}<span style={{ fontSize: 12, fontWeight: 400 }}> lbs</span></div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Current weight</div>
                </div>
                <div className="card" style={{ textAlign: 'center', padding: '12px 8px' }}>
                  <div style={{ fontSize: 22, fontWeight: 700 }}>{latestWeight.body_condition_score || '—'}<span style={{ fontSize: 12, fontWeight: 400 }}> / 9</span></div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Body condition</div>
                </div>
              </div>
            )}

            {dueSoon.length > 0 && (
              <>
                <div className="section-label">Due soon</div>
                <div className="card" style={{ padding: '8px 14px' }}>
                  {dueSoon.map((item, i) => (
                    <div key={i} className="list-row">
                      <span style={{ fontSize: 18 }}>{item.kind === 'vaccine' ? '💉' : item.kind === 'dental' ? '🦷' : '💊'}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>{item.label}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Due {fmt(item.next_due_date)}</div>
                        <DueBar days={item.days} total={item.kind === 'vaccine' ? 365 : item.kind === 'dental' ? 365 : 90} />
                      </div>
                      <DueBadge days={item.days} />
                    </div>
                  ))}
                </div>
              </>
            )}

            <div className="section-label">Quick log</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {[{ label: '💉 Vaccine', modal: 'vaccine' }, { label: '💊 Worming', modal: 'wormer' }, { label: '⚖️ Weight', modal: 'weight' }, { label: '🦷 Dental', modal: 'dental' }, { label: '📅 Appointment', path: `/calendar/add?horse=${id}` }].map(a => (
                <button key={a.label} className="btn btn-secondary" onClick={() => a.modal ? setModal(a.modal) : navigate(a.path)} style={{ fontSize: 13 }}>{a.label}</button>
              ))}
            </div>
          </>
        )}

        {/* DAILY CHECK HISTORY */}
        {tab === 'checks' && (
          <>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 12,
                marginBottom: 10
              }}
            >
              <div>
                <div style={{ fontSize: 15, fontWeight: 700 }}>
                  Daily check history
                </div>

                <div
                  style={{
                    fontSize: 11,
                    color: 'var(--text-muted)',
                    marginTop: 2
                  }}
                >
                  Date/time stamped observations and follow-ups
                </div>
              </div>

              <button
                className="btn btn-sm btn-secondary"
                onClick={() => navigate('/horses/daily-check')}
              >
                Daily Check
              </button>
            </div>

            {checkHistory.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">✓</div>
                <h3>No daily checks yet</h3>
                <p>
                  Daily horse checks will appear here with the person
                  who recorded them and the time they were entered.
                </p>
              </div>
            ) : (
              checkHistory.map(check => {
                const isOpen = expandedCheck === check.id;
                const concern = !!check.is_concern;
                const unresolved =
                  concern && check.concern_status !== 'resolved';

                const detailRows = [
                  ['General condition', 'general_condition'],
                  ['Appetite', 'appetite'],
                  ['Water', 'water'],
                  ['Manure / urination', 'manure_urination'],
                  ['Movement', 'movement'],
                  ['Legs / feet', 'legs_feet'],
                ];

                return (
                  <div
                    key={check.id}
                    className="card"
                    style={{
                      padding: 0,
                      marginBottom: 10,
                      overflow: 'hidden',
                      borderColor: unresolved
                        ? '#E6A04B'
                        : 'var(--border-color)'
                    }}
                  >
                    <button
                      onClick={() =>
                        setExpandedCheck(isOpen ? null : check.id)
                      }
                      style={{
                        width: '100%',
                        border: 'none',
                        background: unresolved ? '#FFF8ED' : 'white',
                        padding: '12px 14px',
                        display: 'flex',
                        gap: 10,
                        alignItems: 'flex-start',
                        textAlign: 'left',
                        cursor: 'pointer'
                      }}
                    >
                      <div
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          flexShrink: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: concern ? '#FFF0DF' : '#EAF5EE',
                          color: concern
                            ? '#C76822'
                            : 'var(--cr-green-dark)',
                          fontWeight: 800
                        }}
                      >
                        {concern ? '!' : '✓'}
                      </div>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 8
                          }}
                        >
                          <div style={{ fontSize: 13, fontWeight: 700 }}>
                            {concern
                              ? checkStatusLabel(check.concern_status)
                              : 'All normal'}
                          </div>

                          <div
                            style={{
                              fontSize: 11,
                              color: 'var(--text-secondary)',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {checkDateTime(check)}
                          </div>
                        </div>

                        <div
                          style={{
                            fontSize: 11,
                            color: concern
                              ? '#B76020'
                              : 'var(--cr-green-dark)',
                            marginTop: 2
                          }}
                        >
                          {concern
                            ? checkConcernSummary(check)
                            : `${check.check_period === 'evening'
                                ? 'Evening'
                                : 'Morning'} check`}
                        </div>

                        <div
                          style={{
                            fontSize: 11,
                            color: 'var(--text-muted)',
                            marginTop: 3
                          }}
                        >
                          Recorded by {check.recorder_name}
                        </div>
                      </div>

                      <span
                        style={{
                          color: 'var(--text-muted)',
                          transform: isOpen
                            ? 'rotate(90deg)'
                            : 'none',
                          transition: 'transform 0.15s'
                        }}
                      >
                        ›
                      </span>
                    </button>

                    {isOpen && (
                      <div
                        style={{
                          borderTop: '1px solid var(--border-color)',
                          padding: '12px 14px',
                          background: 'white'
                        }}
                      >
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: 8
                          }}
                        >
                          {detailRows.map(([label, key]) => (
                            <div
                              key={key}
                              style={{
                                background: 'var(--bg-secondary)',
                                borderRadius: 8,
                                padding: '8px 10px'
                              }}
                            >
                              <div
                                style={{
                                  fontSize: 10,
                                  color: 'var(--text-muted)',
                                  marginBottom: 2
                                }}
                              >
                                {label}
                              </div>

                              <div
                                style={{
                                  fontSize: 12,
                                  fontWeight: 600,
                                  color:
                                    check[key] &&
                                    check[key] !== 'normal'
                                      ? '#B76020'
                                      : 'var(--text-primary)'
                                }}
                              >
                                {detailLabel(key, check[key])}
                              </div>
                            </div>
                          ))}
                        </div>

                        {check.temperature_f && (
                          <div style={{ marginTop: 10, fontSize: 12 }}>
                            <strong>Temperature:</strong>{' '}
                            {check.temperature_f}°F
                          </div>
                        )}

                        {check.notes && (
                          <div style={{ marginTop: 10 }}>
                            <div
                              style={{
                                fontSize: 10,
                                color: 'var(--text-muted)',
                                marginBottom: 3
                              }}
                            >
                              NOTES
                            </div>

                            <div
                              style={{
                                fontSize: 12,
                                whiteSpace: 'pre-wrap'
                              }}
                            >
                              {check.notes}
                            </div>
                          </div>
                        )}

                        {check.photo_url && (
                          <div style={{ marginTop: 10 }}>
                            <img
                              src={check.photo_url}
                              alt="Horse check"
                              style={{
                                width: '100%',
                                maxHeight: 260,
                                objectFit: 'cover',
                                borderRadius: 10
                              }}
                            />
                          </div>
                        )}

                        {check.followups?.length > 0 && (
                          <>
                            <div
                              className="section-label"
                              style={{ marginTop: 14 }}
                            >
                              Follow-ups
                            </div>

                            {check.followups.map(f => (
                              <div
                                key={f.id}
                                style={{
                                  borderLeft:
                                    '3px solid var(--border-color)',
                                  padding: '4px 0 6px 10px',
                                  marginBottom: 8
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: 12,
                                    fontWeight: 700
                                  }}
                                >
                                  {checkStatusLabel(f.status)}
                                </div>

                                <div
                                  style={{
                                    fontSize: 10,
                                    color: 'var(--text-muted)',
                                    marginTop: 1
                                  }}
                                >
                                  {fmt(f.created_at)} ·{' '}
                                  {checkTime(f.created_at)} ·{' '}
                                  {f.recorder_name}
                                </div>

                                {f.notes && (
                                  <div
                                    style={{
                                      fontSize: 12,
                                      marginTop: 3
                                    }}
                                  >
                                    {f.notes}
                                  </div>
                                )}
                              </div>
                            ))}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </>
        )}

        {/* VACCINES */}
        {tab === 'vaccines' && (
          <>
            <button className="btn btn-primary btn-full" onClick={() => setModal('vaccine')} style={{ marginBottom: 12 }}>+ Log vaccine</button>
            {vaccines.length === 0 ? (
              <div className="empty-state"><div className="empty-state-icon">💉</div><h3>No vaccines logged</h3><p>Log your horse's vaccines to track due dates and set reminders</p></div>
            ) : (
              <div className="card" style={{ padding: '8px 14px' }}>
                {vaccines.map(v => (
                  <div key={v.id} className="list-row">
                    <button onClick={async () => { if (!window.confirm('Delete this vaccine record?')) return; try { await api.delete(`/health/${id}/vaccines/${v.id}`); toast.success('Deleted'); load(); } catch { toast.error('Failed'); } }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: 'var(--text-muted)', order: 2, paddingLeft: 8 }}>🗑</button>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{v.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Given {fmt(v.date_given)}{v.given_by ? ` · ${v.given_by}` : ''}</div>
                      {v.next_due_date && (
                        <>
                          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>Next due {fmt(v.next_due_date)}</div>
                          <DueBar days={daysUntil(v.next_due_date)} total={v.frequency_months * 30 || 365} />
                        </>
                      )}
                    </div>
                    {v.next_due_date && <DueBadge days={daysUntil(v.next_due_date)} />}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* WORMERS */}
        {tab === 'wormers' && (
          <>
            <button className="btn btn-primary btn-full" onClick={() => setModal('wormer')} style={{ marginBottom: 12 }}>+ Log worming</button>
            {wormers.length === 0 ? (
              <div className="empty-state"><div className="empty-state-icon">💊</div><h3>No worming logged</h3><p>Log worming to track products used and set rotation reminders</p></div>
            ) : (
              <div className="card" style={{ padding: '8px 14px' }}>
                {wormers.map(w => (
                  <div key={w.id} className="list-row">
                    <button onClick={async () => { if (!window.confirm('Delete this worming record?')) return; try { await api.delete(`/health/${id}/wormers/${w.id}`); toast.success('Deleted'); load(); } catch { toast.error('Failed'); } }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: 'var(--text-muted)', order: 2, paddingLeft: 8 }}>🗑</button>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{w.product_name}</div>
                      {w.active_ingredient && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{w.active_ingredient}</div>}
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 1 }}>Given {fmt(w.date_given)}</div>
                      {w.next_due_date && (
                        <>
                          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>Next due {fmt(w.next_due_date)}</div>
                          <DueBar days={daysUntil(w.next_due_date)} total={(w.interval_weeks || 13) * 7} />
                        </>
                      )}
                    </div>
                    {w.next_due_date && <DueBadge days={daysUntil(w.next_due_date)} />}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* MEDICATIONS */}
        {tab === 'medications' && (
          <>
            <button className="btn btn-primary btn-full" onClick={() => setModal('medication')} style={{ marginBottom: 12 }}>+ Add medication</button>
            {medications.length === 0 ? (
              <div className="empty-state"><div className="empty-state-icon">💊</div><h3>No medications</h3><p>Track current and past medications here</p></div>
            ) : (
              <>
                <div className="section-label">Current</div>
                {medications.filter(m => m.is_active).map(m => (
                  <div key={m.id} className="card" style={{ padding: '10px 14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <div style={{ fontSize: 14, fontWeight: 600 }}>{m.name}</div>
                      {m.is_ongoing ? <span className="badge badge-green">Ongoing</span> : m.end_date ? <span className="badge badge-amber">{daysUntil(m.end_date)}d left</span> : null}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{m.dose_frequency}</div>
                    {m.prescribed_by && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Prescribed by {m.prescribed_by}</div>}
                    {m.reason && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{m.reason}</div>}
                  </div>
                ))}
              </>
            )}
          </>
        )}

        {/* WEIGHT */}
        {tab === 'weight' && (
          <>
            <button className="btn btn-primary btn-full" onClick={() => setModal('weight')} style={{ marginBottom: 12 }}>+ Log weight & body condition</button>
            {weightLogs.length === 0 ? (
              <div className="empty-state"><div className="empty-state-icon">⚖️</div><h3>No weight logged</h3><p>Track body weight and condition score over time</p></div>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
                  <div className="card" style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 24, fontWeight: 700 }}>{latestWeight?.weight_lbs?.toLocaleString()}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>lbs · {fmt(latestWeight?.date)}</div>
                    {weightLogs.length > 1 && (
                      <div style={{ fontSize: 11, color: latestWeight.weight_lbs > weightLogs[1].weight_lbs ? 'var(--cr-green)' : 'var(--cr-red)', marginTop: 4 }}>
                        {latestWeight.weight_lbs > weightLogs[1].weight_lbs ? '▲' : '▼'} {Math.abs(latestWeight.weight_lbs - weightLogs[1].weight_lbs)} lbs
                      </div>
                    )}
                  </div>
                  <div className="card" style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 24, fontWeight: 700 }}>{latestWeight?.body_condition_score || '—'}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>BCS · Henneke scale</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Ideal: 4.5–6</div>
                  </div>
                </div>
                <div className="section-label">History</div>
                <div className="card" style={{ padding: '8px 14px' }}>
                  {weightLogs.map(w => (
                    <div key={w.id} className="list-row">
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>{w.weight_lbs?.toLocaleString()} lbs</div>
                        {w.notes && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{w.notes}</div>}
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{fmt(w.date)}</div>
                        {w.body_condition_score && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>BCS {w.body_condition_score}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
        {/* DENTAL */}
        {tab === 'dental' && (
          <>
            <button className="btn btn-primary btn-full" onClick={() => setModal('dental')} style={{ marginBottom: 12 }}>+ Log dental exam/floating</button>
            {dentalLogs.length === 0 ? (
              <div className="empty-state"><div className="empty-state-icon">🦷</div><h3>No dental records</h3><p>Track exams, floating, findings, and next due reminders</p></div>
            ) : (
              <div className="card" style={{ padding: '8px 14px' }}>
                {dentalLogs.map(d => (
                  <div key={d.id} className="list-row">
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>
                        {d.dental_type === 'exam' ? 'Dental exam' : d.dental_type === 'float' ? 'Floating' : d.dental_type === 'extraction' ? 'Tooth extraction' : d.dental_type === 'other' ? 'Dental - other' : 'Dental exam + floating'}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Performed {fmt(d.date)}{d.performed_by ? ` · ${d.performed_by}` : ''}</div>
                      {d.findings && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{d.findings}</div>}
                      {d.next_due_date && (
                        <>
                          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>Next due {fmt(d.next_due_date)}</div>
                          <DueBar days={daysUntil(d.next_due_date)} total={365} />
                        </>
                      )}
                    </div>
                    {d.next_due_date && <DueBadge days={daysUntil(d.next_due_date)} />}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

      </div>

      {modal === 'vaccine' && <AddVaccineModal horseId={id} onClose={() => setModal(null)} onSave={d => { setVaccines(p => [d, ...p]); setModal(null); }} />}
      {modal === 'wormer'  && <AddWormerModal  horseId={id} onClose={() => setModal(null)} onSave={d => { setWormers(p => [d, ...p]); setModal(null); }} />}
      {modal === 'weight'  && <AddWeightModal  horseId={id} onClose={() => setModal(null)} onSave={d => { setWeightLogs(p => [d, ...p]); setModal(null); }} />}
      {modal === 'medication' && <AddMedicationModal horseId={id} onClose={() => setModal(null)} onSave={d => { setMedications(p => [d, ...p]); setModal(null); }} />}
      {modal === 'dental' && <AddDentalModal horseId={id} onClose={() => setModal(null)} onSave={d => { setDentalLogs(p => [d, ...p]); setModal(null); }} />}
    </div>
  );
};

export default HealthPage;
